import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useFetch } from '../component-items/helpers'
import CardLoading from '../component-items/loading-popover'
import { Pages } from '../component-items/pager'
import GlobalStyles from '../component-items/styles'
import { TableCell, THSort } from '../component-items/datatable'
import { Form } from 'react-bootstrap'
import { UserType } from './RoleBadge'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faTimes,
  faUserPlus,
  faUserMinus,
  faEdit,
  faPlus,
  faMinus,
  faInfoCircle,
  faSearch,
} from '@fortawesome/pro-duotone-svg-icons'

// Types
interface AuditLogEntry {
  id: number
  actor: number | null
  actor_name: string | null
  target_user: number | null
  target_user_name: string | null
  action: string
  target_model: string
  changes: {
    old?: any
    new?: any
    role_name?: string
    segment?: string
    access_level?: string
    [key: string]: any
  }
  reason: string
  timestamp: string
}

interface UserOption {
  id: number
  name: string
  email?: string
}

interface RoleOption {
  id: number
  name: string
}

interface AuditLogTabProps {
  userType: UserType
  warehouseId?: number
  companyId?: number
}

// Styled components
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  align-items: flex-end;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;

  label {
    font-size: 0.8em;
    font-weight: 500;
    color: #6c757d;
  }

  /* Force consistent height for all form controls */
  .form-select,
  select {
    height: 31px !important;
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
`

const SearchableSelect = styled.div`
  position: relative;
  min-width: 180px;
`

const SearchInput = styled.div`
  position: relative;

  input {
    width: 100%;
    height: 31px; /* Match Bootstrap Form.Select size="sm" */
    padding: 0.25rem 0.5rem 0.25rem 1.75rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.875rem;
    line-height: 1.5;

    &:focus {
      outline: none;
      border-color: #86b7fe;
      box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
    }
  }

  svg {
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 0.75em;
  }
`

const DropdownList = styled.ul<{ $show: boolean }>`
  display: ${(props) => (props.$show ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin: 0.25rem 0 0 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  li {
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background: #f8f9fa;
    }

    &.selected {
      background: #e7f1ff;
      font-weight: 500;
    }

    &.no-results {
      color: #6c757d;
      font-style: italic;
      cursor: default;

      &:hover {
        background: white;
      }
    }
  }
`

const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 31px; /* Match Bootstrap Form.Select size="sm" */
  padding: 0.25rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.5;
  background: #e7f1ff;
  cursor: pointer;

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    font-size: 0.75em;

    &:hover {
      color: #dc3545;
    }
  }
`

const ClearFiltersBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  transition: all 0.2s;

  &:hover {
    background: #dc3545;
    color: white;
  }
`

const ActionBadge = styled.span<{ $color: string; $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25em 0.6em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
  background-color: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
`

const DetailsCell = styled.div`
  font-size: 0.9em;
  max-width: 250px;
`

const DetailRow = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: baseline;
  margin-bottom: 0.15rem;
`

const DetailLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
`

const ReasonText = styled.div`
  font-size: 0.9em;
  color: #6c757d;
  font-style: italic;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6c757d;
  text-align: center;

  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h4 {
    margin-bottom: 0.5rem;
    color: #495057;
  }

  p {
    margin: 0;
    font-size: 0.9em;
  }
`

// Action type configuration
const ACTION_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: any }> = {
  assign_role: { label: 'Assigned Role', color: '#0d6efd', bgColor: '#cfe2ff', icon: faUserPlus },
  remove_role: { label: 'Removed Role', color: '#dc3545', bgColor: '#f8d7da', icon: faUserMinus },
  update_role: { label: 'Updated Role', color: '#6f42c1', bgColor: '#e2d9f3', icon: faEdit },
  add_override: { label: 'Added Override', color: '#198754', bgColor: '#d1e7dd', icon: faPlus },
  update_override: { label: 'Updated Override', color: '#fd7e14', bgColor: '#ffe5d0', icon: faEdit },
  remove_override: { label: 'Removed Override', color: '#6c757d', bgColor: '#e9ecef', icon: faMinus },
}

// Date presets
const DATE_PRESETS = [
  { label: 'All Time', value: '' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
]

// Segment labels for filter (matching backend BusinessSegment enum)
const SEGMENT_OPTIONS = [
  { value: '', label: 'All Segments' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'products', label: 'Products' },
  { value: 'vas', label: 'VAS' },
  { value: 'billing', label: 'Billing' },
  { value: 'postage', label: 'Postage' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'settings', label: 'Settings' },
  { value: 'reports', label: 'Reports' },
]

// Format date as "Jan 1, 2025 2:30 PM"
const formatTimestamp = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Format change details
const formatDetails = (entry: AuditLogEntry) => {
  const { action, changes } = entry

  if (action === 'assign_role' || action === 'remove_role' || action === 'update_role') {
    const roleName = changes.new?.role_name || changes.old?.role_name || changes.role_name
    if (action === 'update_role' && changes.old?.role_name && changes.new?.role_name) {
      return (
        <DetailsCell>
          <DetailRow>
            <DetailLabel>Role:</DetailLabel>
            {changes.old.role_name} → {changes.new.role_name}
          </DetailRow>
        </DetailsCell>
      )
    }
    return (
      <DetailsCell>
        <DetailRow>
          <DetailLabel>Role:</DetailLabel>
          {roleName || 'Unknown'}
        </DetailRow>
      </DetailsCell>
    )
  }

  if (action === 'add_override' || action === 'update_override' || action === 'remove_override') {
    const segment = changes.new?.segment || changes.old?.segment || changes.segment
    const oldLevel = changes.old?.access_level
    const newLevel = changes.new?.access_level || changes.access_level

    return (
      <DetailsCell>
        <DetailRow>
          <DetailLabel>Segment:</DetailLabel>
          {segment}
        </DetailRow>
        {action === 'update_override' && oldLevel ? (
          <DetailRow>
            <DetailLabel>Level:</DetailLabel>
            {oldLevel} → {newLevel}
          </DetailRow>
        ) : (
          <DetailRow>
            <DetailLabel>Level:</DetailLabel>
            {newLevel || 'Removed'}
          </DetailRow>
        )}
      </DetailsCell>
    )
  }

  return <DetailsCell>-</DetailsCell>
}

// Calculate start date from preset
const getStartDateFromPreset = (preset: string): string => {
  if (!preset) return ''

  const now = new Date()
  let startDate: Date

  switch (preset) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      break
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      break
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    case '90d':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      break
    default:
      return ''
  }

  return startDate.toISOString().split('T')[0]
}

export const AuditLogTab = ({ userType, warehouseId, companyId }: AuditLogTabProps) => {
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [sort, setSort] = useState<Record<string, string>>({})

  // Filters
  const [datePreset, setDatePreset] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [segmentFilter, setSegmentFilter] = useState('')

  // Searchable filter states
  const [targetUserFilter, setTargetUserFilter] = useState<UserOption | null>(null)
  const [targetUserSearch, setTargetUserSearch] = useState('')
  const [targetUserDropdownOpen, setTargetUserDropdownOpen] = useState(false)

  const [actorFilter, setActorFilter] = useState<UserOption | null>(null)
  const [actorSearch, setActorSearch] = useState('')
  const [actorDropdownOpen, setActorDropdownOpen] = useState(false)

  const [roleFilter, setRoleFilter] = useState<RoleOption | null>(null)
  const [roleSearch, setRoleSearch] = useState('')
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false)

  // Fetch users for filter dropdowns
  const usersUrl =
    userType === 'warehouse' && warehouseId
      ? `/api/warehouse/${warehouseId}/users/`
      : companyId
        ? `/api/company/${companyId}/users/`
        : null

  const { response: usersResp, loaded: usersLoaded }: any = useFetch(usersUrl || '', {}, undefined, !!usersUrl)

  const userOptions: UserOption[] = useMemo(() => {
    if (!usersLoaded || !usersResp) return []
    const users = usersResp?.results || usersResp || []
    return users.map((u: any) => ({
      id: u.id,
      name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email,
      email: u.email,
    }))
  }, [usersResp, usersLoaded])

  // Fetch roles for filter dropdown
  const rolesUrl = `/api/rbac/roles/?user_type=${userType}`
  const { response: rolesResp, loaded: rolesLoaded }: any = useFetch(rolesUrl, {})

  const roleOptions: RoleOption[] = useMemo(() => {
    if (!rolesLoaded || !rolesResp) return []
    const roles = rolesResp?.results || rolesResp || []
    return roles.map((r: any) => ({ id: r.id, name: r.name }))
  }, [rolesResp, rolesLoaded])

  // Filter user options by search
  const filteredTargetUsers = useMemo(() => {
    if (!targetUserSearch) return userOptions
    const search = targetUserSearch.toLowerCase()
    return userOptions.filter((u) => u.name.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search))
  }, [userOptions, targetUserSearch])

  const filteredActors = useMemo(() => {
    if (!actorSearch) return userOptions
    const search = actorSearch.toLowerCase()
    return userOptions.filter((u) => u.name.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search))
  }, [userOptions, actorSearch])

  const filteredRoles = useMemo(() => {
    if (!roleSearch) return roleOptions
    const search = roleSearch.toLowerCase()
    return roleOptions.filter((r) => r.name.toLowerCase().includes(search))
  }, [roleOptions, roleSearch])

  // Build query params
  const params = new URLSearchParams()
  params.set('page', page.toString())

  const startDate = getStartDateFromPreset(datePreset)
  if (startDate) params.set('start_date', startDate)
  if (actionFilter) params.set('action', actionFilter)
  if (targetUserFilter) params.set('target_user_id', targetUserFilter.id.toString())
  if (actorFilter) params.set('actor_id', actorFilter.id.toString())
  if (roleFilter) params.set('role_name', roleFilter.name)
  if (segmentFilter) params.set('segment', segmentFilter)

  // Add context-specific filters for merchant users
  // (backend already filters by company for merchant users, but we could add warehouse_id for warehouse context)
  // For now, the backend handles scoping automatically based on user type

  const apiUrl = `/api/rbac/audit-logs/?${params.toString()}`

  const { response, loaded, error, placeholder }: any = useFetch(apiUrl, {})
  const { count: total, results }: { count: number; results: AuditLogEntry[] } = loaded
    ? response
    : { count: 0, results: [] }

  useEffect(() => {
    if (loaded && results) {
      const pageSize = results.length || 20
      setTotalPages(Math.ceil(total / pageSize) || 1)
    }
  }, [loaded, results, total])

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [datePreset, actionFilter, targetUserFilter, actorFilter, roleFilter, segmentFilter])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.searchable-select')) {
        setTargetUserDropdownOpen(false)
        setActorDropdownOpen(false)
        setRoleDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const clearFilters = () => {
    setDatePreset('')
    setActionFilter('')
    setTargetUserFilter(null)
    setTargetUserSearch('')
    setActorFilter(null)
    setActorSearch('')
    setRoleFilter(null)
    setRoleSearch('')
    setSegmentFilter('')
    setPage(1)
  }

  const hasFilters = datePreset || actionFilter || targetUserFilter || actorFilter || roleFilter || segmentFilter

  if (!loaded) {
    return <CardLoading text={placeholder} error={error} />
  }

  return (
    <div style={{ padding: '0 1em' }}>
      {/* Filters */}
      <FilterBar>
        <FilterGroup>
          <label>Date Range</label>
          <Form.Select size="sm" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
            {DATE_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </Form.Select>
        </FilterGroup>

        <FilterGroup>
          <label>Action Type</label>
          <Form.Select size="sm" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="">All Actions</option>
            {Object.entries(ACTION_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </Form.Select>
        </FilterGroup>

        <FilterGroup>
          <label>Target User</label>
          <SearchableSelect className="searchable-select">
            {targetUserFilter ? (
              <SelectedValue
                onClick={() => {
                  setTargetUserFilter(null)
                  setTargetUserSearch('')
                }}
              >
                <span>{targetUserFilter.name}</span>
                <button type="button">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </SelectedValue>
            ) : (
              <>
                <SearchInput>
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={targetUserSearch}
                    onChange={(e) => setTargetUserSearch(e.target.value)}
                    onFocus={() => setTargetUserDropdownOpen(true)}
                  />
                </SearchInput>
                <DropdownList $show={targetUserDropdownOpen && usersLoaded}>
                  {filteredTargetUsers.length === 0 ? (
                    <li className="no-results">No users found</li>
                  ) : (
                    filteredTargetUsers.slice(0, 10).map((user) => (
                      <li
                        key={user.id}
                        onClick={() => {
                          setTargetUserFilter(user)
                          setTargetUserSearch('')
                          setTargetUserDropdownOpen(false)
                        }}
                      >
                        {user.name} {user.email && <small style={{ color: '#6c757d' }}>({user.email})</small>}
                      </li>
                    ))
                  )}
                </DropdownList>
              </>
            )}
          </SearchableSelect>
        </FilterGroup>

        <FilterGroup>
          <label>Actor (Changed By)</label>
          <SearchableSelect className="searchable-select">
            {actorFilter ? (
              <SelectedValue
                onClick={() => {
                  setActorFilter(null)
                  setActorSearch('')
                }}
              >
                <span>{actorFilter.name}</span>
                <button type="button">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </SelectedValue>
            ) : (
              <>
                <SearchInput>
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={actorSearch}
                    onChange={(e) => setActorSearch(e.target.value)}
                    onFocus={() => setActorDropdownOpen(true)}
                  />
                </SearchInput>
                <DropdownList $show={actorDropdownOpen && usersLoaded}>
                  {filteredActors.length === 0 ? (
                    <li className="no-results">No users found</li>
                  ) : (
                    filteredActors.slice(0, 10).map((user) => (
                      <li
                        key={user.id}
                        onClick={() => {
                          setActorFilter(user)
                          setActorSearch('')
                          setActorDropdownOpen(false)
                        }}
                      >
                        {user.name} {user.email && <small style={{ color: '#6c757d' }}>({user.email})</small>}
                      </li>
                    ))
                  )}
                </DropdownList>
              </>
            )}
          </SearchableSelect>
        </FilterGroup>

        <FilterGroup>
          <label>Role</label>
          <SearchableSelect className="searchable-select">
            {roleFilter ? (
              <SelectedValue
                onClick={() => {
                  setRoleFilter(null)
                  setRoleSearch('')
                }}
              >
                <span>{roleFilter.name}</span>
                <button type="button">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </SelectedValue>
            ) : (
              <>
                <SearchInput>
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    onFocus={() => setRoleDropdownOpen(true)}
                  />
                </SearchInput>
                <DropdownList $show={roleDropdownOpen && rolesLoaded}>
                  {filteredRoles.length === 0 ? (
                    <li className="no-results">No roles found</li>
                  ) : (
                    filteredRoles.map((role) => (
                      <li
                        key={role.id}
                        onClick={() => {
                          setRoleFilter(role)
                          setRoleSearch('')
                          setRoleDropdownOpen(false)
                        }}
                      >
                        {role.name}
                      </li>
                    ))
                  )}
                </DropdownList>
              </>
            )}
          </SearchableSelect>
        </FilterGroup>

        <FilterGroup>
          <label>Segment</label>
          <Form.Select size="sm" value={segmentFilter} onChange={(e) => setSegmentFilter(e.target.value)}>
            {SEGMENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </FilterGroup>

        {hasFilters && (
          <ClearFiltersBtn onClick={clearFilters}>
            <FontAwesomeIcon icon={faTimes} />
            Clear Filters
          </ClearFiltersBtn>
        )}
      </FilterBar>

      {/* Table */}
      {results.length === 0 ? (
        <EmptyState>
          <FontAwesomeIcon icon={hasFilters ? faFilter : faInfoCircle} />
          <h4>{hasFilters ? 'No matching results' : 'No permission changes yet'}</h4>
          <p>
            {hasFilters
              ? 'Try adjusting your filters to find what you are looking for.'
              : 'Permission changes will appear here when users roles or overrides are modified.'}
          </p>
          {hasFilters && (
            <ClearFiltersBtn onClick={clearFilters} style={{ marginTop: '1rem' }}>
              <FontAwesomeIcon icon={faTimes} />
              Clear Filters
            </ClearFiltersBtn>
          )}
        </EmptyState>
      ) : (
        <>
          <GlobalStyles.DataTable>
            <thead>
              <tr>
                <THSort title="Timestamp" sortKey="timestamp" {...{ sort, setSort }} />
                <th>
                  <span className="text">Actor</span>
                </th>
                <th>
                  <span className="text">Target User</span>
                </th>
                <th>
                  <span className="text center">Action</span>
                </th>
                <th>
                  <span className="text">Details</span>
                </th>
                <th>
                  <span className="text">Reason</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map((entry) => {
                const actionConfig = ACTION_CONFIG[entry.action] || {
                  label: entry.action,
                  color: '#6c757d',
                  bgColor: '#e9ecef',
                  icon: faInfoCircle,
                }

                return (
                  <tr key={entry.id}>
                    <TableCell input={formatTimestamp(entry.timestamp)} />
                    <TableCell input={entry.actor_name || 'System'} />
                    <TableCell input={entry.target_user_name || 'Unknown'} />
                    <TableCell
                      center
                      input={
                        <ActionBadge $color={actionConfig.color} $bgColor={actionConfig.bgColor}>
                          <FontAwesomeIcon icon={actionConfig.icon} />
                          {actionConfig.label}
                        </ActionBadge>
                      }
                    />
                    <TableCell input={formatDetails(entry)} />
                    <TableCell
                      input={
                        entry.reason ? (
                          <GlobalStyles.Tooltip data-title={entry.reason}>
                            <ReasonText>{entry.reason}</ReasonText>
                          </GlobalStyles.Tooltip>
                        ) : (
                          '-'
                        )
                      }
                    />
                  </tr>
                )
              })}
            </tbody>
          </GlobalStyles.DataTable>
          <br />
          <Pages page={page} setPage={(p: number) => setPage(p)} total={totalPages} />
          <br />
        </>
      )}
    </div>
  )
}
